<template>
  <div>
    <app-input
      ref="input"
      v-model="modelDisplay"
      :label="title"
      :readonly="!displayKeyOnly"
      :info-button-id="infoButtonId"
      :info-button-key="infoButtonKey"
      :required="isRequired"
      @blur="onModelDisplayBlur"
    >
      <template #after>
        <app-button icon="filter_list" :disable="disableComputed" :readonly="readonlyComputed" dense @click="modalVisible = true" />
      </template>
      <template v-if="modelValue?.length && !disableComputed && !readonlyComputed" #append>
        <q-icon name="cancel" class="cursor-pointer" color="grey" @click.stop.prevent="clear" />
      </template>
    </app-input>
    <app-dialog v-if="modalVisible" min-width="600px" @ok="saveAndExit" @cancel="modalVisible = false">
      <template #title>
        {{ title }}
      </template>
      <template #content>
        <div class="column q-gutter-md">
          <div class="row q-gutter-sm">
            <div class="col-grow">
              <app-input v-model="search" :label="$t('_RSFAP_SHARED_FILTERS_SEARCH')" />
            </div>
            <app-button @click="search = null" :label="$t('_RSFAP_SHARED_CLEAR_DATA')" />
          </div>
          <app-table
            :rows="filteredItems"
            :columns="[
              { name: 'formatted', label: '' },
              { name: 'selected', label: $t('_RSFAP_SHARED_SELECTED') }
            ]"
            height="330px"
            row-key="Key"
          >
            <template #body-cell-selected="props">
              <q-td :props="props">
                <app-checkbox v-model="props.row.selected" />
              </q-td>
            </template>
            <template #header-cell-selected="props">
              <q-th :props="props" class="text-center" auto-width>
                <app-checkbox v-model="allItemsSelected" dark />
              </q-th>
            </template>
          </app-table>
        </div>
      </template>
    </app-dialog>
  </div>
</template>

<script setup>
import { ref, watch, computed, reactive } from "vue"
import { storeToRefs } from "pinia"

import { useDictionariesStore } from "@/stores/dictionaries"
import { useTranslationsStore } from "@/stores/translations.js"
import { useModuleDataStore } from "@/stores/moduleData.js"

import { useSelection } from "@/composables/useSelection.js"
import { useFieldsetState } from "@/composables/useFieldsetState.js"

const props = defineProps({
  dictionaryName: String,
  additionalItems: {
    type: Array,
    default: () => []
  },
  modelValue: Array,
  required: Boolean,
  disable: Boolean,
  readonly: Boolean,
  sortByKey: Boolean,
  sortByValue: Boolean,
  title: String,
  showValueOnly: Boolean,
  displayKeyOnly: Boolean,
  whiteList: Array,
  infoButtonId: String,
  infoButtonKey: String,
  requiredCondition: Boolean,
  requiredConditionKey: String
})

const emits = defineEmits(["update:modelValue"])

const dictionariesStore = useDictionariesStore()
const moduleStore = useModuleDataStore()

const modalVisible = ref(false)
const modelDisplay = ref("")
const search = ref("")

const input = ref(null)

const { disableComputed, readonlyComputed } = useFieldsetState(props)

const isRequired = computed(() => {
  if (props.required) {
    return true
  }

  if (props.requiredCondition || props.requiredConditionKey) {
    const conditionKey = props.requiredConditionKey || props.infoButtonKey
    return moduleStore.moduleSettings.requiredFields?.includes(conditionKey)
  }

  return false
})

const { changingLanguage } = storeToRefs(useTranslationsStore())

const items = computed(() => {
  let list = props.additionalItems ? [...props.additionalItems] : []
  const dictionary = dictionariesStore.getDictionary(props.dictionaryName)

  if (props.whiteList) {
    list = dictionary.filter((item) => props.whiteList.includes(item.Key))
  } else {
    list = list.concat(dictionary)
  }

  if (props.sortByKey) {
    list.sort((a, b) => a.Key.localeCompare(b.Key))
  }
  if (props.sortByValue) {
    list.sort((a, b) => a.Value.localeCompare(b.Value))
  }

  return list.map((item) =>
    reactive({
      ...item,
      formatted: props.displayKeyOnly ? item.Key : props.showValueOnly ? item.Value : `${item.Key} - ${item.Value}`,
      selected: props.modelValue?.includes(item.Key)
    })
  )
})

const { allSelected: allItemsSelected } = useSelection(items)

const filteredItems = computed(() => {
  if (!search.value) {
    return items.value
  }
  return items.value.filter((item) => item.formatted.toLowerCase().includes(search.value.toLowerCase()))
})

const onModelDisplayBlur = () => {
  const parsedKeysInput = modelDisplay.value.split(",").map((key) => key.trim())
  const newModel = parsedKeysInput.filter((key) => findKey(key))

  emits("update:modelValue", newModel)
}

const prepareDisplay = () => {
  if (!props.modelValue) {
    return
  }
  const valuesArray = props.modelValue.map((key) => {
    if (props.displayKeyOnly) {
      return key
    }
    const additionalItem = props.additionalItems.find((item) => item.Key === key)
    return additionalItem ? additionalItem.Value : dictionariesStore.getValueForKey(props.dictionaryName, key)
  })

  modelDisplay.value = valuesArray.join()
}

const findKey = (keyInput) => {
  if (props.additionalItems && props.additionalItems.some((item) => item.Key === keyInput)) {
    return keyInput
  }
  return dictionariesStore.isKeyInDictionary(props.dictionaryName, keyInput) ? keyInput : null
}

const saveAndExit = () => {
  const selectedKeys = items.value.filter((item) => item.selected).map((item) => item.Key)

  emits("update:modelValue", selectedKeys)
  modalVisible.value = false
}

const clear = () => {
  emits("update:modelValue", [])
}

watch(() => props.modelValue, prepareDisplay, { immediate: true })
watch(() => changingLanguage.value, prepareDisplay)

defineExpose({
  validate: () => input.value.validate()
})
</script>

<style lang="scss" scoped>
//Prevent overflowing of row container
.col-grow {
  flex-basis: 0;
}
</style>
